import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  DollarSign,
  TrendingUp,
  Clock,
  BarChart3,
  Activity,
  Target,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Minimize2,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Download,
} from 'lucide-react';
import { formatCurrency, formatHours, formatPercent } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import type { CostCentre } from './cost-centres-table';
import { 
  fetchCostCentreTransactions, 
  fetchCostCentreBreakdown,
  type CostCentreTransaction,
  type CostCentreBreakdown,
} from '@/lib/data-service';

interface CostCentreFullModalProps {
  costCentre: CostCentre | null;
  projectId: number;
  allCostCentres: CostCentre[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCostCentreChange: (costCentre: CostCentre) => void;
  onMinimize?: () => void;
}

export function CostCentreFullModal({
  costCentre,
  projectId,
  allCostCentres,
  open,
  onOpenChange,
  onCostCentreChange,
  onMinimize,
}: CostCentreFullModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCostCentreSelector, setShowCostCentreSelector] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteDialogType, setDeleteDialogType] = useState<'has-transactions' | 'has-budget' | 'clean'>('clean');
  const selectorRef = useRef<HTMLDivElement>(null);
  
  // State for cost centre transactions and breakdown (fetched from API)
  const [recentTransactions, setRecentTransactions] = useState<CostCentreTransaction[]>([]);
  const [costBreakdown, setCostBreakdown] = useState<CostCentreBreakdown>({
    labour: 0,
    expenses: 0,
    materials: 0,
    subcontractors: 0,
  });
  const [_isLoadingData, setIsLoadingData] = useState(false);

  // Reset tab when modal opens or cost centre changes
  useEffect(() => {
    if (open) {
      setActiveTab('overview');
      setIsActive(costCentre?.isActive ?? true);
      setIsEditMode(false);
    }
  }, [open, costCentre?.id, costCentre?.isActive]);

  // Fetch cost centre transactions and breakdown when cost centre changes
  useEffect(() => {
    if (!costCentre || !open) return;
    
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const [transactions, breakdown] = await Promise.all([
          fetchCostCentreTransactions(costCentre.id, projectId),
          fetchCostCentreBreakdown(costCentre.id, projectId, costCentre.actualCosts),
        ]);
        setRecentTransactions(transactions);
        setCostBreakdown(breakdown);
      } catch {
        console.error('Failed to fetch cost centre data');
        // Reset to empty state on error
        setRecentTransactions([]);
        setCostBreakdown({
          labour: 0,
          expenses: 0,
          materials: 0,
          subcontractors: 0,
        });
      } finally {
        setIsLoadingData(false);
      }
    };
    
    fetchData();
  }, [costCentre?.id, projectId, costCentre?.actualCosts, open]);

  // Handle status toggle
  const handleStatusToggle = async (checked: boolean) => {
    if (!costCentre) return;
    
    setIsUpdatingStatus(true);
    const statusText = checked ? 'active' : 'inactive';
    
    try {
      // Optimistic update
      setIsActive(checked);
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update the cost centre
      const updatedCostCentre = { ...costCentre, isActive: checked };
      onCostCentreChange(updatedCostCentre);
      
      toast.success(`Cost centre marked as ${statusText}`, {
        duration: 3000,
      });
    } catch (error) {
      // Revert on error
      setIsActive(!checked);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handle edit action
  const handleEdit = () => {
    setIsEditMode(true);
    setActiveTab('overview'); // Switch to overview tab for editing
  };

  // Handle delete action
  const handleDelete = () => {
    if (!costCentre) return;
    
    // TODO: Replace with actual logic to check transactions and budget
    const hasTransactions = false; // Check if cost centre has transactions
    const hasBudget = Math.random() > 0.5; // Check if cost centre has budget allocations
    
    if (hasTransactions) {
      setDeleteDialogType('has-transactions');
    } else if (hasBudget) {
      setDeleteDialogType('has-budget');
    } else {
      setDeleteDialogType('clean');
    }
    
    setShowDeleteDialog(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!costCentre) return;
    
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.success(
        <div className="flex items-center gap-2">
          <span>Cost centre moved to Recycle Bin</span>
        </div>,
        {
          duration: 5000,
          action: {
            label: 'Undo',
            onClick: () => {
              toast.success('Cost centre restored');
            },
          },
        }
      );
      
      // Close modal and return to list
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to delete cost centre. Please try again.');
    } finally {
      setShowDeleteDialog(false);
    }
  };

  // Handle mark as inactive (when cannot delete)
  const handleMarkInactive = async () => {
    if (!costCentre) return;
    
    setShowDeleteDialog(false);
    await handleStatusToggle(false);
  };

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowCostCentreSelector(false);
      }
    };
    
    if (showCostCentreSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCostCentreSelector]);

  // Navigation helpers
  const currentIndex = costCentre ? allCostCentres.findIndex(cc => cc.id === costCentre.id) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allCostCentres.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      onCostCentreChange(allCostCentres[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onCostCentreChange(allCostCentres[currentIndex + 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open || showCostCentreSelector || !costCentre) return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    };

    if (open && costCentre) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, showCostCentreSelector, costCentre, handlePrevious, handleNext]);

  if (!costCentre) return null;

  // Filter cost centres based on search
  const filteredCostCentres = allCostCentres.filter(cc => 
    cc.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate metrics
  const totalCosts = costCentre.actualCosts + costCentre.committedCosts;
  const costVariance = costCentre.costsBudget - costCentre.actualCosts;
  const costPercentUsed = costCentre.costsBudget > 0 
    ? (costCentre.actualCosts / costCentre.costsBudget) * 100 
    : 0;
  const hoursVariance = costCentre.hoursBudget - costCentre.actualHours;
  const hoursPercentUsed = costCentre.hoursBudget > 0
    ? (costCentre.actualHours / costCentre.hoursBudget) * 100
    : 0;
  const remainingHours = costCentre.hoursBudget - costCentre.actualHours;
  
  // Revenue metrics
  const revenueVariance = costCentre.actualRevenue - costCentre.revenueBudget;
  const revenuePercentAchieved = costCentre.revenueBudget > 0 
    ? (costCentre.actualRevenue / costCentre.revenueBudget) * 100 
    : 0;
  const remainingRevenue = costCentre.revenueBudget - costCentre.actualRevenue;
  const profitMargin = costCentre.actualRevenue > 0
    ? ((costCentre.actualRevenue - costCentre.actualCosts) / costCentre.actualRevenue) * 100
    : 0;

  // Calculate burn rate (simplified - costs per month)
  const monthsElapsed = 3; // Approximate months elapsed
  const burnRate = costCentre.actualCosts / monthsElapsed;

  const getBudgetStatusColor = (percent: number) => {
    if (percent < 75) return 'text-success-600 dark:text-success-500';
    if (percent < 90) return 'text-warning-600 dark:text-warning-500';
    return 'text-error-600 dark:text-error-500';
  };

  const handleCostCentreSelect = (cc: CostCentre) => {
    onCostCentreChange(cc);
    setShowCostCentreSelector(false);
    setSearchQuery('');
  };

  return (
    <Dialog open={open && !!costCentre} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col">
        {/* Fixed Header with Cost Centre Selector */}
        <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-2xl font-bold mb-2">{costCentre.name}</DialogTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-sans font-medium">{costCentre.code}</span>
                  {costCentre.type && (
                    <>
                      <span>•</span>
                      <span>{costCentre.type}</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Status Toggle */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isActive}
                          onCheckedChange={handleStatusToggle}
                          disabled={isUpdatingStatus}
                          aria-label="Toggle cost centre status"
                        />
                        <span className={cn(
                          "text-sm font-medium transition-colors",
                          isActive ? "text-success-600 dark:text-success-500" : "text-muted-foreground"
                        )}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Toggle cost centre status</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="w-px h-6 bg-border" />

                {/* Previous Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  className="px-3"
                  title="Previous Cost Centre (←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Cost Centre Switcher Dropdown */}
                <div className="relative" ref={selectorRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCostCentreSelector(!showCostCentreSelector)}
                    className="min-w-[200px] justify-between"
                  >
                    <span className="truncate">Switch Cost Centre</span>
                    <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
                  </Button>
                
                  {showCostCentreSelector && (
                    <div className="absolute right-0 top-full mt-2 w-[400px] bg-background border rounded-lg shadow-lg z-50">
                      <div className="p-3 border-b">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search cost centres..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-9"
                          />
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto">
                        <div className="p-2">
                          {filteredCostCentres.length === 0 ? (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                              No cost centres found
                            </div>
                          ) : (
                            filteredCostCentres.map((cc) => (
                              <button
                                key={cc.id}
                                onClick={() => handleCostCentreSelect(cc)}
                                className={cn(
                                  "w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors",
                                  cc.id === costCentre.id && "bg-accent"
                                )}
                              >
                                <div className="font-medium text-sm">{cc.name}</div>
                                <div className="text-xs text-muted-foreground font-sans">{cc.code}</div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="p-2 border-t bg-muted/50 text-xs text-muted-foreground text-center">
                        {filteredCostCentres.length} of {allCostCentres.length} cost centres
                      </div>
                    </div>
                  )}
                </div>

                {/* Next Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  disabled={!hasNext}
                  className="px-3"
                  title="Next Cost Centre (→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

                {/* Separator */}
                <Separator orientation="vertical" className="h-6" />

                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="px-3">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleEdit}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled className="text-muted-foreground">
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                      <span className="ml-auto text-xs">Soon</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled className="text-muted-foreground">
                      <Download className="h-4 w-4 mr-2" />
                      Export Data
                      <span className="ml-auto text-xs">Soon</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="w-px h-6 bg-border" />

                {/* Minimize Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMinimize}
                  title="Return to sheet view"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full justify-start rounded-none border-b px-6 flex-shrink-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="budget">Budget Management</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="forecasting">Forecasting</TabsTrigger>
            <TabsTrigger value="allocations">Allocations</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Tab 1: Overview */}
            <TabsContent value="overview" className="p-6 space-y-6 mt-0">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        Costs
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold">{formatCurrency(costCentre.actualCosts)}</span>
                        <span className="text-sm text-muted-foreground">of {formatCurrency(costCentre.costsBudget)}</span>
                      </div>
                      <Progress value={Math.min(costPercentUsed, 100)} className="h-2" />
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm font-medium", getBudgetStatusColor(costPercentUsed))}>
                          {formatPercent(costPercentUsed, 1)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs",
                          costPercentUsed < 75 ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                          costPercentUsed < 90 ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                          'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
                        )}>
                          {costPercentUsed < 75 ? 'On Track' : costPercentUsed < 90 ? 'At Risk' : 'Over Budget'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold">{formatCurrency(costCentre.actualRevenue)}</span>
                        <span className="text-sm text-muted-foreground">of {formatCurrency(costCentre.revenueBudget)}</span>
                      </div>
                      <Progress value={Math.min(revenuePercentAchieved, 100)} className="h-2" />
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm font-medium", 
                          revenuePercentAchieved >= 90 ? 'text-success-600 dark:text-success-500' :
                          revenuePercentAchieved >= 75 ? 'text-warning-600 dark:text-warning-500' :
                          'text-error-600 dark:text-error-500'
                        )}>
                          {formatPercent(revenuePercentAchieved, 1)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs",
                          revenuePercentAchieved >= 90 ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                          revenuePercentAchieved >= 75 ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                          'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
                        )}>
                          {revenuePercentAchieved >= 90 ? 'Excellent' : revenuePercentAchieved >= 75 ? 'Good' : 'Below Target'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        Hours
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold">{costCentre.actualHours.toLocaleString()}h</span>
                        <span className="text-sm text-muted-foreground">of {costCentre.hoursBudget.toLocaleString()}h</span>
                      </div>
                      <Progress value={Math.min(hoursPercentUsed, 100)} className="h-2" />
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm font-medium", getBudgetStatusColor(hoursPercentUsed))}>
                          {formatPercent(hoursPercentUsed, 1)}
                        </span>
                        <span className={cn("text-sm font-medium", remainingHours >= 0 ? 'text-success-600' : 'text-error-600')}>
                          {remainingHours >= 0 ? '+' : ''}{remainingHours.toLocaleString()}h
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Cost Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Cost Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(costBreakdown).map(([category, amount]) => {
                        const percentage = costCentre.actualCosts > 0 ? (amount / costCentre.actualCosts) * 100 : 0;
                        return (
                          <div key={category} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium capitalize">{category}</span>
                              <span className="text-sm text-muted-foreground">
                                {formatCurrency(amount)} ({formatPercent(percentage, 0)})
                              </span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Recent Transactions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recentTransactions.slice(0, 5).map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell className="text-sm">
                              {new Date(transaction.date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                              })}
                            </TableCell>
                            <TableCell className="text-sm">{transaction.description}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{transaction.category}</Badge>
                            </TableCell>
                            <TableCell className="text-sm font-medium text-right">
                              {formatCurrency(transaction.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tab 2: Budget Management */}
              <TabsContent value="budget" className="p-6 space-y-6 mt-0">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Budget Management</h3>
                    <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Budget Allocation</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Cost Budget</Label>
                          <Input type="number" value={costCentre.costsBudget} disabled />
                          <p className="text-xs text-muted-foreground">Budget allocation management coming soon</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Revenue Budget</Label>
                          <Input type="number" value={costCentre.revenueBudget} disabled />
                          <p className="text-xs text-muted-foreground">Revenue target management coming soon</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Hours Budget</Label>
                          <Input type="number" value={costCentre.hoursBudget} disabled />
                          <p className="text-xs text-muted-foreground">Resource allocation management coming soon</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Budget Adjustments</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="rounded-lg border p-4 space-y-2">
                          <p className="text-sm font-medium">Adjustment History</p>
                          <p className="text-xs text-muted-foreground">
                            Track budget changes and approvals
                          </p>
                          <Button variant="outline" size="sm" disabled className="w-full mt-2">
                            View History
                          </Button>
                        </div>
                        <div className="rounded-lg border p-4 space-y-2">
                          <p className="text-sm font-medium">Request Budget Change</p>
                          <p className="text-xs text-muted-foreground">
                            Submit budget adjustment requests
                          </p>
                          <Button variant="outline" size="sm" disabled className="w-full mt-2">
                            Request Change
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Budget Controls & Thresholds</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="rounded-lg border p-4">
                          <p className="text-sm font-medium mb-2">Alert Thresholds</p>
                          <p className="text-xs text-muted-foreground mb-4">
                            Configure automatic alerts when budget utilisation reaches specific thresholds
                          </p>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs">Warning (75%)</Label>
                              <Input type="number" value="75" disabled className="h-8 text-sm" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Critical (90%)</Label>
                              <Input type="number" value="90" disabled className="h-8 text-sm" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Block (100%)</Label>
                              <Input type="number" value="100" disabled className="h-8 text-sm" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Tab 3: Performance */}
              <TabsContent value="performance" className="p-6 space-y-6 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Financial Performance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Cost Variance</span>
                          <span className={cn("font-medium", costVariance >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Revenue Variance</span>
                          <span className={cn("font-medium", revenueVariance >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {revenueVariance >= 0 ? '+' : ''}{formatCurrency(revenueVariance)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Profit Margin</span>
                          <span className={cn("font-medium", profitMargin >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {formatPercent(profitMargin, 1)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Burn Rate</span>
                          <span className="font-medium">{formatCurrency(burnRate)}/month</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Resource Utilization</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Hours Variance</span>
                          <span className={cn("font-medium", hoursVariance >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {hoursVariance >= 0 ? '+' : ''}{hoursVariance.toLocaleString()}h
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Remaining Hours</span>
                          <span className="font-medium">{Math.abs(remainingHours).toLocaleString()}h</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Cost per Hour</span>
                          <span className="font-medium">
                            {costCentre.actualHours > 0 ? formatCurrency(costCentre.actualCosts / costCentre.actualHours) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Tab 4: Insights */}
              <TabsContent value="insights" className="p-6 space-y-6 mt-0">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    Key Observations
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-success-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Budget Performance</p>
                          <p className="text-sm text-muted-foreground">
                            This cost centre is tracking {costPercentUsed < 100 ? 'within' : 'over'} budget at {formatPercent(costPercentUsed, 1)} utilisation.
                            {costPercentUsed < 75 && ' Performance is excellent with significant budget remaining.'}
                            {costPercentUsed >= 75 && costPercentUsed < 90 && ' Monitor closely as budget approaches limit.'}
                            {costPercentUsed >= 90 && ' Immediate attention required to prevent budget overrun.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {costPercentUsed >= 90 && (
                      <div className="rounded-lg border border-error-200 bg-error-50 dark:bg-error-950 dark:border-error-800 p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-error-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium text-error-900 dark:text-error-100">Budget Alert</p>
                            <p className="text-sm text-error-700 dark:text-error-300">
                              Cost centre has exceeded 90% of allocated budget. Review spending and consider budget reallocation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Tab 5: Forecasting - Coming Soon */}
              <TabsContent value="forecasting" className="p-6 space-y-6 mt-0">
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Target className="h-12 w-12 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">Forecasting & Projections</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md">
                    Advanced forecasting features including trend analysis, budget projections, and predictive insights will be available soon.
                  </p>
                  <Badge variant="outline">Coming Soon</Badge>
                </div>
              </TabsContent>

              {/* Tab 6: Allocations - Coming Soon */}
              <TabsContent value="allocations" className="p-6 space-y-6 mt-0">
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <BarChart3 className="h-12 w-12 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">Resource Allocations</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md">
                    Detailed resource allocation management, team assignments, and capacity planning features will be available soon.
                  </p>
                  <Badge variant="outline">Coming Soon</Badge>
                </div>
              </TabsContent>
          </div>
        </Tabs>
      </DialogContent>

      {/* Delete Confirmation Dialogs */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          {deleteDialogType === 'has-transactions' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning-600" />
                  Cannot Delete Cost Centre
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This cost centre has recorded transactions and cannot be deleted.
                  <br /><br />
                  You can mark it as inactive using the toggle switch instead.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction onClick={() => setShowDeleteDialog(false)}>
                  Got it
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}

          {deleteDialogType === 'has-budget' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  Delete Cost Centre?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  "{costCentre?.name}" has budget allocations but no transactions.
                  It will be moved to the Recycle Bin and can be restored within 30 days.
                  <br /><br />
                  Are you sure you want to proceed?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Move to Recycle Bin
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}

          {deleteDialogType === 'clean' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  Delete Cost Centre?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  "{costCentre?.name}" will be moved to the Recycle Bin.
                  You can restore it within 30 days.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Move to Recycle Bin
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
